import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Brain, Star, AlertTriangle, Sparkles, Lightbulb, Check } from "lucide-react";

function InsightList({ items, Icon, accentClass }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="space-y-2">
      {items.map((text) => (
        <div key={text} className="flex items-start gap-2 text-xs text-gray-300">
          <Icon className={`w-3 h-3 ${accentClass} shrink-0 mt-0.5`} />
          <span>{text}</span>
        </div>
      ))}
    </div>
  );
}

const gradeColor = (grade) => {
  if (!grade) return "text-gray-400";
  if (grade.startsWith('A')) return "text-[#00FFA3]";
  if (grade.startsWith('B')) return "text-[#F59E0B]";
  return "text-[#FF3B30]";
};

const riskColor = (risk) => {
  if (risk === 'Low') return "text-[#00FFA3]";
  if (risk === 'Medium') return "text-[#F59E0B]";
  if (risk === 'High') return "text-[#FF3B30]";
  return "text-gray-400";
};

export default function AITradeJournal({ journal, journalLoading, onAnalyze }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.5 }}
      className="mt-8"
    >
      <Card className="glass-effect">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-white flex items-center gap-2" style={{ fontFamily: 'Unbounded' }}>
              <Brain className="w-5 h-5 text-[#6366F1]" />
              AI TRADE JOURNAL
            </CardTitle>
            <Button
              data-testid="refresh-journal-btn"
              size="sm"
              variant="outline"
              onClick={onAnalyze}
              disabled={journalLoading}
              className="border-[#6366F1]/30 text-[#6366F1] hover:bg-[#6366F1]/10 text-xs"
            >
              {journalLoading ? "Analyzing..." : "Analyze My Trades"}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {!journal ? (
            <div className="text-center py-8 text-gray-500 text-sm">
              <Brain className="w-10 h-10 mx-auto mb-3 text-gray-600" />
              <p>Click "Analyze My Trades" to get AI-powered insights on your trading patterns.</p>
              <p className="text-xs mt-1 text-gray-600">Requires at least 2 closed trades</p>
            </div>
          ) : !journal.has_data ? (
            <div className="text-center py-8 text-gray-500 text-sm">
              <Brain className="w-10 h-10 mx-auto mb-3 text-gray-600" />
              <p>{journal.message}</p>
            </div>
          ) : (
            <div className="space-y-5">
              {/* Trading Style Header */}
              <div className="glass-effect p-4 rounded-lg border border-[#6366F1]/20 text-center">
                <div className="text-xs text-gray-400 uppercase tracking-wider mb-1">Your Trading Style</div>
                <h3 className="text-xl sm:text-2xl font-bold text-[#6366F1]" style={{ fontFamily: 'Unbounded' }}>
                  {journal.ai_insights?.trading_style || "Trader"}
                </h3>
                <p className="text-sm text-gray-400 mt-1">{journal.ai_insights?.style_description}</p>
                <div className="flex items-center justify-center gap-4 mt-3">
                  <div className="text-center">
                    <div className={`text-2xl font-bold mono ${gradeColor(journal.ai_insights?.overall_grade)}`}>
                      {journal.ai_insights?.overall_grade || "N/A"}
                    </div>
                    <div className="text-[10px] text-gray-500">Grade</div>
                  </div>
                  <div className="w-px h-8 bg-white/10" />
                  <div className="text-center">
                    <div className={`text-2xl font-bold mono ${riskColor(journal.ai_insights?.risk_profile)}`}>
                      {journal.ai_insights?.risk_profile || "N/A"}
                    </div>
                    <div className="text-[10px] text-gray-500">Risk</div>
                  </div>
                </div>
              </div>

              {/* Strengths & Weaknesses */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="glass-effect p-4 rounded-lg border border-[#00FFA3]/10">
                  <div className="flex items-center gap-2 mb-3">
                    <Star className="w-4 h-4 text-[#00FFA3]" />
                    <h4 className="text-sm font-bold text-[#00FFA3]">Strengths</h4>
                  </div>
                  <InsightList items={journal.ai_insights?.strengths} Icon={Check} accentClass="text-[#00FFA3]" />
                </div>

                <div className="glass-effect p-4 rounded-lg border border-[#FF3B30]/10">
                  <div className="flex items-center gap-2 mb-3">
                    <AlertTriangle className="w-4 h-4 text-[#FF3B30]" />
                    <h4 className="text-sm font-bold text-[#FF3B30]">Areas to Improve</h4>
                  </div>
                  <InsightList items={journal.ai_insights?.weaknesses} Icon={AlertTriangle} accentClass="text-[#FF3B30]" />
                </div>
              </div>

              {/* AI Insights */}
              <div className="glass-effect p-4 rounded-lg border border-[#00E0FF]/10">
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles className="w-4 h-4 text-[#00E0FF]" />
                  <h4 className="text-sm font-bold text-[#00E0FF]">AI Insights</h4>
                </div>
                <InsightList items={journal.ai_insights?.insights} Icon={Sparkles} accentClass="text-[#00E0FF]" />
              </div>

              {/* Actionable Tips */}
              <div className="glass-effect p-4 rounded-lg border border-[#F59E0B]/10">
                <div className="flex items-center gap-2 mb-3">
                  <Lightbulb className="w-4 h-4 text-[#F59E0B]" />
                  <h4 className="text-sm font-bold text-[#F59E0B]">Tips to Improve</h4>
                </div>
                <InsightList items={journal.ai_insights?.tips} Icon={Lightbulb} accentClass="text-[#F59E0B]" />
              </div>

              <p className="text-[10px] text-gray-600 text-center">
                Analysis generated at {new Date(journal.generated_at).toLocaleString()} — refreshes when you have new closed trades
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
}
